import {
  Card,
  BlockStack,
  Checkbox,
  InlineStack,
  Icon,
  Tooltip,
  Popover,
  ActionList,
  Button,
} from "@medentic/polaris-react";
import { InfoIcon, ViewIcon, LockIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { useMyBrand } from "@features/events/hooks/useMyBrand";
import { StatusEntityType } from "@features/events/types";

const VISIBILITY_ICON_MAP = {
  0: LockIcon,
  1: ViewIcon,
} as const;

interface StatusCardProps {
  entityType: StatusEntityType;
  featured: boolean | null;
  verified: boolean;
  visibility: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataChange?: (updates: any) => void;
}

// Configuration map for entity-specific translations
const ENTITY_CONFIG = {
  [StatusEntityType.EVENT]: {
    featured: {
      label: "eventDetails.sidebar.featuredEvent",
      help: "eventDetails.sidebar.featuredEventHelp",
    },
    verified: {
      label: "eventDetails.sidebar.verifiedEvent",
      help: "eventDetails.sidebar.verifiedEventHelp",
    },
    visibility: {
      label: "eventDetails.sidebar.visibility",
      disabledHelp: "eventDetails.sidebar.visibilityDisabledHelp",
    },
  },
  [StatusEntityType.INSTRUCTOR]: {
    featured: {
      label: "instructorDetails.status.featured.label",
      help: "instructorDetails.status.featured.helpText",
    },
    verified: {
      label: "instructorDetails.status.verified.label",
      help: "instructorDetails.status.verified.helpText",
    },
    visibility: {
      label: "instructorDetails.status.visibility.label",
      disabledHelp: "eventDetails.sidebar.visibilityDisabledHelp",
    },
  },
  [StatusEntityType.BRAND]: {
    featured: {
      label: "brandDetails.status.featured.label",
      help: "brandDetails.status.featured.helpText",
    },
    verified: {
      label: "brandDetails.status.verified.label",
      help: "brandDetails.status.verified.helpText",
    },
    visibility: {
      label: "brandDetails.status.visibility.label",
      disabledHelp: "brandDetails.status.visibility.disabledHelp",
    },
  },
} as const;

export default function StatusCard({
  entityType = StatusEntityType.EVENT,
  featured,
  verified,
  visibility,
  onDataChange,
}: StatusCardProps) {
  // All hooks must be called before any conditional returns (Rules of Hooks)
  const { eventOrganizerId: hcc } = useParams<{
    eventOrganizerId: string;
  }>();
  
  const { t } = useTranslation();
  const { visibilityOptions: metadataVisibilityOptions } = useEventMetadata();
  const [popoverActive, setPopoverActive] = useState(false);
  
  // For EVENT entity type, check brand verification status to determine if visibility can be changed
  const { myBrand, isLoading: isBrandLoading } = useMyBrand(
    hcc,
    entityType === StatusEntityType.EVENT
  );

  // Get entity-specific configuration
  const config = ENTITY_CONFIG[entityType];

  const togglePopover = useCallback(() => setPopoverActive((active) => !active), []);

  // Visibility options with fallback
  const visibilityOptions = useMemo(() => {
    if (metadataVisibilityOptions.length > 0) {
      return metadataVisibilityOptions;
    }
    
    return [
      { 
        label: t("eventDetails.visibility.private"), 
        value: "0",
        description: t("eventDetails.visibility.privateDescription"),
        icon: VISIBILITY_ICON_MAP[0]
      },
      { 
        label: t("eventDetails.visibility.public"), 
        value: "1",
        description: t("eventDetails.visibility.publicDescription"),
        icon: VISIBILITY_ICON_MAP[1]
      },
    ];
  }, [metadataVisibilityOptions, t]);

  // Get current selection for button display
  const currentSelection = useMemo(() => {
    const currentValue = visibility?.toString() || "0";
    return visibilityOptions.find(option => option.value === currentValue) || visibilityOptions[0];
  }, [visibility, visibilityOptions]);

  const handleVisibilityChange = useCallback((value: string) => {
    onDataChange?.({
      visibility: parseInt(value) || 0,
    });
    setPopoverActive(false);
  }, [onDataChange]);

  // Show loading state while fetching brand data (only for EVENT entity type)
  if (entityType === StatusEntityType.EVENT && isBrandLoading) {
    return (
      <Card>
        <BlockStack gap="200">
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--p-color-text-subdued)' }}>
            {t("common.loading")}
          </div>
        </BlockStack>
      </Card>
    );
  }

  // Determine if visibility control should be disabled (only for events)
  const isVisibilityDisabled: boolean = entityType === StatusEntityType.EVENT && !myBrand?.verified;

  const shouldShowFeatured: boolean = featured !== null && featured !== undefined;

  return (
    <Card>
      <BlockStack gap="200">
        {shouldShowFeatured && (
          <InlineStack gap="200" align="start" blockAlign="center">
            <Checkbox
              label={t(config.featured.label)}
              checked={featured || false}
              disabled
            />
            <Tooltip content={t(config.featured.help)}>
              <Icon source={InfoIcon} tone="subdued" />
            </Tooltip>
          </InlineStack>
        )}

        <InlineStack gap="200" align="start" blockAlign="center">
          <Checkbox
            label={t(config.verified.label)}
            checked={verified || false}
            disabled
          />
          <Tooltip content={t(config.verified.help)}>
            <Icon source={InfoIcon} tone="subdued" />
          </Tooltip>
        </InlineStack>

        <BlockStack gap="100">
          <InlineStack gap="200" align="start" blockAlign="center">
            <label style={{ fontSize: '14px', fontWeight: '500', color: 'var(--p-color-text)' }}>
              {t(config.visibility.label)}
            </label>
            {isVisibilityDisabled && (
              <Tooltip content={t(config.visibility.disabledHelp)}>
                <Icon source={InfoIcon} tone="subdued" />
              </Tooltip>
            )}
          </InlineStack>
          <Popover
            active={popoverActive}
            activator={
              <Button 
                onClick={togglePopover} 
                disclosure
                fullWidth
                size="large"
                icon={currentSelection.icon}
                disabled={isVisibilityDisabled}
              >
                {currentSelection.label}
              </Button>
            }
            autofocusTarget="first-node"
            onClose={togglePopover}
          >
            <ActionList
              actionRole="menuitem"
              sections={[
                {
                  title: t(config.visibility.label),
                  items: visibilityOptions.map((option) => ({
                    content: option.label,
                    helpText: option.description,
                    icon: option.icon,
                    onAction: () => handleVisibilityChange(option.value),
                  })),
                },
              ]}
            />
          </Popover>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
